import { Box, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import Button from '../../../../components/Button/Button';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import DateCalendar from '../../../../components/DateCalendar/DateCalendar';
import { updateRequest } from '../../../../stores/NewConnectionStore';
import { RootState } from '../../../../stores/Store';
import useNewConnectionScheduleDateUtils from './useNewConnectionScheduleDateUtils';
import useModal from '../../../../hooks/useModal/useModal';
import Alert from '../../../../components/Alert/Alert';

interface NewConnectionScheduleDateStepProps {
  nextStep: () => void;
  onClose: () => void;
}
function NewConnectionScheduleDateStep({ nextStep, onClose }: NewConnectionScheduleDateStepProps) {
  const [t] = useTranslation();
  const { startDate, endDate } = useSelector((state: RootState) => state.newConnectionReducer.request);
  const dispatch = useDispatch();
  const { validateDateRange, today, tomorrow, maxStartDate, maxEndDate } = useNewConnectionScheduleDateUtils();
  const modal = useModal();

  const formik = useFormik({
    initialValues: { startDate, endDate },
    enableReinitialize: false,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: yup.object({
      startDate: yup.string().nullable(),
      endDate: yup
        .string()
        .test('dateRange', t('common.inputValidations.dateRange'), (value, context) =>
          validateDateRange(value, context, 'startDate'),
        )
        .nullable(),
    }),
    onSubmit: (values) => {
      dispatch(
        updateRequest({
          startDate: values.startDate,
          endDate: values.endDate,
        }),
      );
      nextStep();
    },
  });

  /**
   * Checks if we need to show a message if end date is 30 days before start date
   */
  const shouldShowWarningMessage = (): boolean => {
    if (formik.isValid && formik.values.endDate) {
      const startRange = DateTime.fromISO(formik.values.startDate || DateTime.now().toISODate());
      const endRange = DateTime.fromISO(formik.values.endDate);
      return endRange.startOf('day') < startRange.plus({ days: 30 }).startOf('day');
    }
    return false;
  };

  /**
   * Displays the confirmation modal
   */
  const showConfirmationModal = () => {
    modal.showModal({
      modalTitle: '',
      modalDescription: (
        <>
          <Box width="100%" sx={{ marginBottom: '1rem' }}>
            <Typography>{t('pages.eSimManager.newConnectionDrawer.schedule30dayWarning1')}</Typography>
          </Box>
          <Box width="100%">
            <Typography>{t('pages.eSimManager.newConnectionDrawer.schedule30dayWarning2')}</Typography>
          </Box>
        </>
      ),
      confirmationModal: true,
      confirmationButtonLabel: t('common.yes'),
      onAccept: (setOpen) => {
        setOpen(false);
        formik.submitForm();
      },
      cancelButtonLabel: t('common.no'),
      onDiscard: () => {
        formik.setFieldValue('endDate', '');
      },
    });
  };

  /**
   * Checks if warning message should be shown before procceeding
   */
  const submitHandler = () => {
    if (shouldShowWarningMessage()) {
      showConfirmationModal();
    } else {
      formik.submitForm();
    }
  };

  return (
    <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ marginBottom: '2.5rem' }}>
        <Typography sx={{ marginBottom: '2rem' }}>
          {t('pages.eSimManager.newConnectionDrawer.scheduleDescription')}
        </Typography>
        <Box sx={{ input: { width: '13.375rem' } }}>
          <DateCalendar
            onChange={(date) => {
              formik.setFieldValue('startDate', date?.toISODate() as string);
            }}
            label={t('pages.eSimManager.newConnectionDrawer.startDate')}
            value={formik.values.startDate ? DateTime.fromISO(formik.values.startDate) : null}
            formStyle={{ marginBottom: '1rem' }}
            placeholder={DateTime.now().toFormat('    dd    |    LL    |    y    ')}
            minDate={today}
            maxDate={maxStartDate}
            disablePast
          />
          <Box sx={{ marginBottom: '1rem' }}>
            <Alert
              alertType="info"
              message={<Trans>{t('pages.eSimManager.newConnectionDrawer.scheduleWarning')}</Trans>}
              fontSize="0.875rem"
            />
          </Box>
          <DateCalendar
            onChange={(date) => {
              formik.setFieldValue('endDate', date?.toISODate() as string);
            }}
            error={Boolean(formik.errors.endDate)}
            label={t('pages.eSimManager.newConnectionDrawer.endDateField')}
            feedback={formik.errors.endDate ? formik.errors.endDate : ''}
            value={formik.values.endDate ? DateTime.fromISO(formik.values.endDate) : null}
            minDate={tomorrow}
            maxDate={maxEndDate}
            disablePast
          />
        </Box>
      </Box>
      {/* <Alert */}
      {/*  alertType="info" */}
      {/*  message={<Trans>{t('pages.eSimManager.newConnectionDrawer.scheduleWarning')}</Trans>} */}
      {/*  fontSize="0.875rem" */}
      {/* /> */}
      <Box sx={{ marginTop: 'auto' }}>
        <ButtonGroup>
          <Button label={t('common.cancel')} color="alternative2Button" onClick={onClose} />
          <Button
            label={t('common.continue')}
            color="primaryButton"
            disabled={!formik.isValid}
            onClick={submitHandler}
          />
        </ButtonGroup>
      </Box>
    </form>
  );
}

export default NewConnectionScheduleDateStep;
